import { inject } from "inversify";
import debounce from "lodash/debounce";
import { action, makeObservable, observable } from "mobx";
import type { DownloadFormat } from "./ChatMessagesStore";
import { ChatMessagesStore } from "./ChatMessagesStore";
export class MessagesTabsLocalStore {
  searchText = "";
  focusMessageId = "";
  isDeleteMode = false;
  isDownloadMode = false;
  downloadFormat: DownloadFormat = "pdf";
  selectedMessages: Set<string> = new Set();
  showScrollToBottom = false;
  tempDeletedMessages: Set<string> = new Set();
  constructor(@inject(ChatMessagesStore) private chatMessagesStore: ChatMessagesStore) {
    makeObservable(
      this,
      {
        searchText: observable,
        setSearchText: action,
        focusMessageId: observable,
        setFocusMessageId: action,
        isDeleteMode: observable,
        isDownloadMode: observable,
        downloadFormat: observable,
        toggleDeleteMode: action,
        toggleDownloadMode: action,
        selectedMessages: observable,
        setSelectedMessages: action,
        setSelectAllMessages: action,
        closeDeleteMode: action,
        deleteMessages: action,
        setShowScrollToBottom: action,
        showScrollToBottom: observable,
      },
      { autoBind: true }
    );
  }

  setSearchText(text: string) {
    this.searchText = text;
  }

  setFocusMessageId(id: string) {
    this.focusMessageId = id;
  }

  setShowScrollToBottom(val: boolean) {
    this.showScrollToBottom = val;
  }

  toggleDeleteMode() {
    this.isDeleteMode = !this.isDeleteMode;
  }

  toggleDownloadMode() {
    this.isDownloadMode = !this.isDownloadMode;
  }

  setSelectAllMessages(tab: "group" | "personal") {
    if (this.chatMessagesStore.messages.state !== "fulfilled") return;

    if (this.selectedMessages.size > 0) {
      this.selectedMessages.clear();
    } else {
      const messages =
        tab === "group" && this.chatMessagesStore.group.state === "fulfilled"
          ? this.chatMessagesStore.group.value.group?.messages
          : this.chatMessagesStore.messagesFiltered.state === "fulfilled"
            ? this.chatMessagesStore.messagesFiltered.value
            : [];

      messages?.forEach((msg) => {
        this.setSelectedMessages(msg.id);
      });
    }
  }

  setSelectedMessages(id: string) {
    this.selectedMessages.has(id) ? this.selectedMessages.delete(id) : this.selectedMessages.add(id);
    this.selectedMessages = new Set(this.selectedMessages);
  }

  closeDeleteMode() {
    this.isDeleteMode = false;
    this.selectedMessages = new Set();
  }

  deleteMessages() {
    this.chatMessagesStore.deleteMessages(Array.from(this.selectedMessages));
    this.tempDeletedMessages = new Set([...this.selectedMessages, ...this.tempDeletedMessages]);
    this.closeDeleteMode();
  }

  closeDownloadMode() {
    this.isDownloadMode = false;
    this.selectedMessages = new Set();
  }

  downloadMessages(format: "csv" | "pdf") {
    this.chatMessagesStore.downloadMessages(Array.from(this.selectedMessages), format);
    this.closeDownloadMode();
  }

  setOnScroll(el: HTMLDivElement) {
    const onScroll = debounce(
      (event: Event) => {
        event.stopPropagation();
        this.setShowScrollToBottom(el.scrollHeight - el.clientHeight !== Math.ceil(el.scrollTop));
      },
      100,
      { maxWait: 500 }
    );
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }
}
